import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, CheckCircle2, Plus, ChevronRight, TrendingUp, Loader2 } from "lucide-react";

// Shows the most recent case's Documentation Strength and nudges the client to
// upload the exact items that raise their Crash Ready Score.
export default function DocumentationStrengthCard({ cases }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const target = (cases || []).find((c) => c.status === "active") || (cases || [])[0];

  useEffect(() => {
    if (!target) { setLoading(false); return; }
    api.get(`/cases/${target.case_id}/documentation`)
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [target?.case_id]);

  if (loading) {
    return (
      <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex items-center gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading documentation strength…
      </div>
    );
  }
  if (!target || !data || data.score == null) return null;

  const met = data.factors.filter((f) => f.met);
  const missing = (data.missing || []).slice(0, 4);
  const pct = data.score;
  const color = pct >= 85 ? "text-emerald-600" : pct >= 70 ? "text-cobalt" : "text-amber-600";
  const bar = pct >= 85 ? "bg-emerald-500" : pct >= 70 ? "bg-cobalt" : "bg-amber-400";

  return (
    <div className="mt-6 bg-white rounded-3xl border border-slate-100 p-6 md:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="documentation-strength-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-cobalt-light flex items-center justify-center shrink-0">
            <TrendingUp className="w-6 h-6 text-cobalt" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-navy">Documentation Strength</h2>
            <p className="text-sm text-slate-500 mt-0.5">Stronger documentation = a stronger claim. Case {data.case_number}.</p>
          </div>
        </div>
        <div className={`text-right shrink-0 ${color}`}>
          <div className="font-heading text-3xl font-extrabold leading-none" data-testid="doc-strength-score">{pct}</div>
          <div className="text-xs font-semibold">Grade {data.grade}</div>
        </div>
      </div>

      <div className="mt-5 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${bar}`} style={{ width: `${pct}%` }} />
      </div>

      {missing.length > 0 ? (
        <div className="mt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Raise your score</p>
          <div className="space-y-2" data-testid="doc-strength-nudges">
            {missing.map((f) => (
              <Link key={f.key} to={`/new-accident?case=${target.case_id}`} data-testid={`doc-nudge-${f.key}`}
                className="group flex items-center justify-between gap-3 rounded-2xl border border-slate-100 hover:border-cobalt/40 bg-slate-50/60 hover:bg-cobalt-light/40 p-3.5 transition-all">
                <span className="flex items-center gap-2.5 text-sm font-medium text-navy">
                  <span className="h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center"><Plus className="w-3.5 h-3.5 text-cobalt" /></span>
                  {f.nudge}
                </span>
                <span className="flex items-center gap-1 text-xs font-bold text-emerald-600">+{f.points}<ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-cobalt transition-colors" /></span>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex items-center gap-2 text-sm text-emerald-700" data-testid="doc-strength-complete">
          <CheckCircle2 className="w-4 h-4" /> Excellent — your documentation is fully complete.
        </div>
      )}

      {met.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="doc-strength-met">
          {met.map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-50 text-emerald-600 px-2.5 py-1 rounded-full">
              <ShieldCheck className="w-3 h-3" /> {f.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
